import {
  EPaymentStatus,
  ICalculatedResidentAutoApplyPaymentDTO,
  ICalculateScheduledPaymentDetailsDTO,
  ICalculateScheduledPaymentDetailsResultDTO,
  ICollectionHistory,
  ICollectionTask,
  ICollectionTaskCalledResidentDTO,
  ICollectionTaskChangeEscalationDTO,
  ICollectionTaskSendMailDTO,
  ICollectionTasksFilters,
  ICollectionTasksResult,
  ICreateResidentUserWithTempPasswordDTO,
  IOneTimePaymentDTO,
  IPayment,
  IPaymentTransactionEffectiveDate,
  IResident,
  IResidentContact,
  IResidentCreatePaymentMethodDTO,
  IResidentEventHistoryFiltersDTO,
  IResidentFiltersDTO,
  IResidentOpenBalanceDTO,
  IResidentPaymentMethod,
  IResidentPreferencesDto,
  IResidentStatisticsDTO,
  ISchedulePaymentSetting,
  ISetupWizardFacilityResidentContactSettings,
  IUser,
  ITransactionToPay,
  IResidentAuthCode
} from '@zunta/configurations'
import { IBaseStore } from '../types'
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import Api from '@/api'
import { ICollectionTaskPostponeDTO } from '@zunta/configurations/dist/types/data-transfer-object/collection-flow-dto'
import { IAutoPaySetting } from '@zunta/configurations/dist/types/payments'

export interface IResidentState {
  residents: IResident[];
  total: number;
  filters: IResidentFiltersDTO;
  typeOfContactList: Array<string>;
  residentContacts: Array<IResidentContact>
  statistics: IResidentStatisticsDTO | null;
  selectedResident: IResident | null;
  openBalance: IResidentOpenBalanceDTO | null;
  calculatedAutoApplyPayment: ICalculatedResidentAutoApplyPaymentDTO | null;
  selectedPayment: Partial<IPayment> | null;
  selectedPaymentTransactions: Partial<IPaymentTransactionEffectiveDate>[];
  scheduledPaymentDetails: ICalculateScheduledPaymentDetailsResultDTO | null;
  selectedScheduledPayment: ISchedulePaymentSetting | null;
  selectedAutoPayment: IAutoPaySetting | null;
  residentsInCollections: IResident[];
  collectionTasks: ICollectionTask[];
  collectionTasksTotal: number;
  collectionHistory: ICollectionHistory[];
  collectionHistoryTotal: number;
  paymentMethods: IResidentPaymentMethod[];
  residentCollectionTask: ICollectionTask | null;
  chainTypeOfContactListMap: {[k: string]: string[]}
}

export interface IResidentStore extends IBaseStore {
  state: IResidentState
}
@Module({
  name: 'resident',
  namespaced: true
})
export class ResidentModule extends VuexModule {
  residents: IResident[] = [];
  total: number = 0;

  // totalQuickSearch: number = 0;
  residentsQuickSearch: IResident[] = [];
  isAllQuickSearchPagesLoaded: boolean = false

  filters: IResidentFiltersDTO = {
    skip: 0,
    limit: 20
  }
  chainTypeOfContactListMap: {[k: string]: string[]} = {};
  typeOfContactList: Array<string> = [];
  residentContacts: IResidentContact[] = [];
  statistics: IResidentStatisticsDTO | null = null;
  selectedResident: IResident | null = null;
  openBalance: IResidentOpenBalanceDTO | null = null;
  calculatedAutoApplyPayment: ICalculatedResidentAutoApplyPaymentDTO | null = null;
  selectedPayment: Partial<IPayment> | null = null;
  selectedPaymentTransactions: ITransactionToPay[] = [];
  scheduledPaymentDetails: ICalculateScheduledPaymentDetailsResultDTO | null = null;
  selectedScheduledPayment: ISchedulePaymentSetting | null = null;
  selectedAutoPayment: IAutoPaySetting | null = null;
  residentsInCollections: IResident[] = [];
  collectionTasks: ICollectionTask[] = [];
  collectionTasksTotal: number = 0;
  collectionHistory: ICollectionHistory[] = [];
  collectionHistoryTotal: number = 0;
  paymentMethods: IResidentPaymentMethod[] = [];
  residentCollectionTask: ICollectionTask | null = null;
  collectionTasksWithAgingItems: any = [];
  collectionTasksWithAgingTotal: number = 0;
  agingTransactionEffectiveDates: string[] = [];
  collectionTasksWithAgingFilters: any = {};
  residentQuickSearchPageSize: number = 20;
  residentQuickSearchCurrentLimit: number = this.residentQuickSearchPageSize;
  residentCodeRecord?: IResidentAuthCode

  @Mutation
  SET_CHAIN_CONTACT_TYPE_LIST_MAP (map: {[k: string]: string[]}) {
    this.chainTypeOfContactListMap = map
  }

  @Mutation
  SET_RESIDENT_CODE_RECORD (code: IResidentAuthCode) {
    this.residentCodeRecord = code
  }

  @Mutation
  SET_RESIDENT_COLLECTION_TASK (collectionTask: ICollectionTask | null) {
    this.residentCollectionTask = collectionTask
  }

  @Mutation
  DELETE_PAYMENT_METHOD (paymentMethodId: number) {
    this.paymentMethods = this.paymentMethods.filter((paymentMethod: IResidentPaymentMethod) => {
      return paymentMethod.id !== paymentMethodId
    })
  }

  @Mutation
  SET_PAYMENT_METHODS (paymentMethods: IResidentPaymentMethod[]) {
    this.paymentMethods = paymentMethods
  }

  @Mutation
  SET_FILTERS (filters: IResidentFiltersDTO) {
    this.filters = filters
  }

  @Mutation
  DELETE_FILTER (field: keyof IResidentFiltersDTO) {
    delete this.filters[field]
  }

  @Mutation
  ADD_PAYMENT_METHOD (paymentMethod: IResidentPaymentMethod) {
    this.paymentMethods.unshift(paymentMethod)
  }

  @Mutation
  MAKE_PAYMENT_METHOD_PRIMARY (paymentMethodId: number) {
    this.paymentMethods = this.paymentMethods.map((paymentMethod: IResidentPaymentMethod) => {
      return {
        ...paymentMethod,
        isPrimary: paymentMethod.id === paymentMethodId
      }
    })
  }

  @Mutation
  ADD_ITEM_TO_COLLECTION_HISTORY (item: ICollectionHistory) {
    this.collectionHistory.push(item)
  }

  @Mutation
  SET_COLLECTION_HISTORY (items: ICollectionHistory[]) {
    this.collectionHistory = items
  }

  @Mutation
  UPDATE_COLLECTION_TASK_ASSIGNED_USER (input: { collectionTaskId: number, assignedUser: IUser }) {
    for (let i = 0; i < this.collectionTasks.length; i++) {
      const collectionTask = this.collectionTasks[i]
      if (collectionTask.id === input.collectionTaskId) {
        collectionTask.assignedUser = input.assignedUser
        break
      }
    }
  }

  @Mutation
  UPDATE_RESIDENT_COLLECTION_TASK (task: ICollectionTask) {
    this.collectionTasks = this.collectionTasks.map((eCollectionTask: ICollectionTask) => {
      if ((typeof eCollectionTask.resident === 'number' ? eCollectionTask.resident : eCollectionTask.resident.id) === task.resident as number) {
        return {
          ...eCollectionTask,
          noteFrom: task.noteFrom,
          note: task.note,
          noteTime: task.noteTime,
          taskDate: task.taskDate,
          isEscalated: task.isEscalated,
          id: task.id,
          assignedUser: task.assignedUser,
          createdAt: task.createdAt,
          updatedAt: task.updatedAt
        }
      }
      return eCollectionTask
    })
  }

  @Mutation
  SET_COLLECTION_HISTORY_TOTAL (total: number) {
    this.collectionHistoryTotal = total
  }

  @Mutation
  SET_COLLECTION_TASKS (collectionTasks: ICollectionTask[]) {
    this.collectionTasks = collectionTasks
  }

  @Mutation
  SET_COLLECTION_TASKS_TOTAL (total: number) {
    this.collectionTasksTotal = total
  }

  @Mutation
  SET_COLLECTION_WITH_AGING_TASKS (collectionTasks: any) {
    this.collectionTasksWithAgingItems = collectionTasks
  }

  @Mutation
  SET_COLLECTION_TASKS_WITH_AGING_TOTAL (total: number) {
    this.collectionTasksWithAgingTotal = total
  }

  @Mutation
  SET_COLLECTION_TASKS_WITH_AGING_TED_DATES (dates: string[]) {
    this.agingTransactionEffectiveDates = dates
  }

  @Mutation
  SET_COLLECTION_WITH_AGING_FILTERS (filters: any) {
    this.collectionTasksWithAgingFilters = filters
  }

  @Mutation
  SET_RESIDENTS_IN_COLLECTIONS (residents: IResident[]) {
    this.residentsInCollections = residents
  }

  // @Mutation
  // SET_SELECTED_SCHEDULED_PAYMENT (scheduledPayment: ISchedulePaymentSetting | null) {
  //   this.selectedScheduledPayment = scheduledPayment
  // }

  @Mutation
  SET_SELECTED_AUTO_PAYMENT (autoPayment: IAutoPaySetting | null) {
    this.selectedAutoPayment = autoPayment
  }

  @Mutation
  SET_SCHEDULED_PAYMENT_DETAILS (data: ICalculateScheduledPaymentDetailsResultDTO | null) {
    this.scheduledPaymentDetails = data
  }

  @Mutation
  SET_SELECTED_PAYMENT (payment: Partial<IPayment> | null) {
    this.selectedPayment = payment
  }

  @Mutation
  SET_SELECTED_PAYMENT_TRANSACTIONS (transactions: ITransactionToPay[]) {
    this.selectedPaymentTransactions = transactions
  }

  @Mutation
  SET_CALCULATED_AUTO_APPLY_PAYMENT (calculatedAutoApplyPayment: ICalculatedResidentAutoApplyPaymentDTO | null) {
    this.calculatedAutoApplyPayment = calculatedAutoApplyPayment
  }

  @Mutation
  SET_OPEN_BALANCE (openBalance: IResidentOpenBalanceDTO | null) {
    this.openBalance = openBalance
  }

  @Mutation
  UPDATE_RESIDENt_TOTAL (total: number) {
    this.total = total
  }

  @Mutation
  UPDATE_RESIDENT_LIST (residents: IResident[]) {
    this.residents = residents
  }

  // @Mutation
  // UPDATE_RESIDENt_TOTAL_QUICK_SEARCH (total: number) {
  //   this.totalQuickSearch = total
  // }

  @Mutation
  UPDATE_RESIDENT_LIST_QUICK_SEARCH (residents: IResident[]) {
    this.residentsQuickSearch = residents
  }

  @Mutation
  UPDATE_ALL_QUICK_SEARCH_PAGES_LOADED (isLastPage: boolean) {
    this.isAllQuickSearchPagesLoaded = isLastPage
  }

  @Mutation
  RESET_RESIDENT_QUICK_SEARCH_CURRENT_LIMIT () {
    this.residentQuickSearchCurrentLimit = this.residentQuickSearchPageSize
  }

  @Mutation
  UPDATE_RESIDENT_QUICK_SEARCH_LIMIT (limit: number) {
    this.residentQuickSearchCurrentLimit = limit
  }

  @Mutation
  UPDATE_RESINDET_TYPE_OF_CONTACT_LIST (typeOfContactList: Array<string>) {
    this.typeOfContactList = typeOfContactList
  }

  @Mutation
  UPDATE_RESIDENT_CONTACTS (residentContacts: IResidentContact[]) {
    // this.residentContacts = residentContacts.sort((a: IResidentContact, b: IResidentContact) => {
    //   return a.id - b.id
    // })
    this.residentContacts = residentContacts
  }

  @Mutation
  UPDATE_SELECTED_RESIDENT (resident: IResident | null) {
    this.selectedResident = resident
  }

  @Mutation
  UPDATE_RESIDENT_SETTINGS (settings: IResidentPreferencesDto) {
    if (this.selectedResident) {
      Object.keys(settings).forEach((key: string) => {
        (this.selectedResident as any)[key] = (settings as any)[key]
      })
    }
  }

  @Mutation
  SET_RESIDENTS_STATISTICS (statistics: IResidentStatisticsDTO) {
    this.statistics = statistics
  }

  @Mutation
  RESET_RESIDENT_INFO () {
    this.residentContacts = []
  }

  @Mutation
  EDIT_PAYMENT_METHOD (response: IResidentPaymentMethod) {
    this.paymentMethods = this.paymentMethods.map((pm) => {
      if (pm.id === response.id) {
        return response
      }
      return pm
    })
  }

  @Action
  setFilters (filters: IResidentFiltersDTO) {
    this.SET_FILTERS(filters)
  }

  @Action
  deleteFilter (field: string) {
    this.DELETE_FILTER(field as keyof IResidentFiltersDTO)
  }

  @Action
  async getResidentsFilteredList (filters: IResidentFiltersDTO) {
    const { total, residents } = await Api.resident.getResidentFilteredList(filters)
    this.UPDATE_RESIDENT_LIST(residents)
    this.UPDATE_RESIDENt_TOTAL(total)
  }

  @Action
  async getResidentsFilteredListForQuickSearch (filters: IResidentFiltersDTO) {
    if (!filters.limit) filters.limit = 20

    const { total, residents } = await Api.resident.getResidentFilteredList(filters)
    const isLastPage: boolean = residents.length === total
    this.UPDATE_RESIDENT_LIST_QUICK_SEARCH(residents)
    this.UPDATE_ALL_QUICK_SEARCH_PAGES_LOADED(isLastPage)
  }

  @Action
  async getAndReturnResidentsFilteredList (filters: IResidentFiltersDTO) {
    // const { total, residents } = await Api.resident.getResidentFilteredList(filters)
    const { residents } = await Api.resident.getResidentFilteredList(filters)
   return residents
  }

  @Action
  async fetchResidentTypeOfContactList (facilityId: number) {
    const contactTypes = await Api.resident.getResidentTypeOfContactList(facilityId)
    if (Array.isArray(contactTypes)) {
      this.UPDATE_RESINDET_TYPE_OF_CONTACT_LIST(contactTypes)
    }
  }

  @Action
  async fetchResidentContacts (residentId: number | string) {
    const residentContacts: IResidentContact[] = await Api.resident.getResidentContacts(residentId)
    this.UPDATE_RESIDENT_CONTACTS(residentContacts)
  }

  @Action
  async fetchResidentById (residentId: number | string) {
    const resident: IResident = await Api.resident.getResidentById(residentId)
    this.UPDATE_SELECTED_RESIDENT(resident)
    return resident
  }

  @Action
  async pushResidentPageToMirror (pushPayload: { residentId: string | number, mirrorRoomId: string }) {
    const { residentId, mirrorRoomId } = pushPayload
    return Api.resident.pushResidentPageToMirror(residentId, mirrorRoomId)
  }

  @Action
  async checkForActiveMirror (mirrorRoomId: string) {
    const isActive = await Api.resident.checkForActiveMirror(mirrorRoomId) === 'OK'
    return isActive
  }

  @Action
  async makeResidentContactForMailing (input: { residentId: number, contactId: number }) {
    const residentContacts: IResidentContact[] = await Api.resident.makeResidentContactForMailing(input.residentId, input.contactId)
    if (!(residentContacts as any).statusCode) {
      this.UPDATE_RESIDENT_CONTACTS(residentContacts)
    }
  }

  @Action
  async makeResidentContactPrimary (input: { residentId: number, contactId: number }) {
    const residentContacts: IResidentContact[] = await Api.resident.makeResidentContactPrimary(input.residentId, input.contactId)
    if (!(residentContacts as any).statusCode) {
      this.UPDATE_RESIDENT_CONTACTS(residentContacts)
    }
  }

  @Action
  async updateResidentSettings (input: { residentId: number, settings: Partial<IResident>, affectedBatch?: number }) {
    const response = await Api.resident.updateResidentSettings(input.residentId, input.settings, input.affectedBatch)
    if (response === 'OK') {
      this.UPDATE_RESIDENT_SETTINGS(input.settings)
    }
    return response
  }

  @Action
  resetResidentInfo () {
    this.RESET_RESIDENT_INFO()
  }

  @Action
  resetSelectedResident () {
    this.UPDATE_SELECTED_RESIDENT(null)
  }

  @Action
  async fetchStatistics () {
    const statistics = await Api.statistics.getResidentStatistics()
    if (!statistics.statusCode) {
      this.SET_RESIDENTS_STATISTICS(statistics)
    }
  }

  @Action
  async initResidentContactsUsedForMailing (input: {
    facilityId: number | string,
    order: string[],
    override: boolean
  }) {
    await Api.resident.initResidentContactsUsedForMailing(input.facilityId, input.order, input.override)
  }

  @Action
  async fetchResidentOpenBalance (residentId: number | string) {
    const openBalance = await Api.resident.getResidentOpenBalance(residentId)
    if (!(openBalance as any).statusCode) {
      this.SET_OPEN_BALANCE(openBalance)
    }
  }

  @Action
  async getResidentAutoApplyPaymentCalculation (input: { residentId: number | string, amount: number }) {
    const response = await Api.payments.getResidentAutoApplyPaymentCalculation(input.residentId, input.amount)
    if (!(response as any).statusCode) {
      this.SET_CALCULATED_AUTO_APPLY_PAYMENT(response)
    }
  }

  @Action
  resetResidentAutoApplyPayment () {
    this.SET_CALCULATED_AUTO_APPLY_PAYMENT(null)
  }

  @Action
  async makeOneTimePayment (input: { residentId: number | string, data: IOneTimePaymentDTO }): Promise<any> {
    const response: any = await Api.payments.makeOneTimePayment(input.residentId, input.data)
    if (response && !response.statusCode) {
      const status = response.paymentStatus ? response.paymentStatus.status : EPaymentStatus.DECLINED
      return { status: status === EPaymentStatus.DECLINED ? 'Failed' : 'OK', payment: response, message: [] }
    }
    return { status: 'Failed', message: Array.isArray(response.message) ? response.message : [response.message] }
  }

  @Action
  async makeHMOPayment (input: { facilityId: number | string, data: IOneTimePaymentDTO }): Promise<any> {
    const response: any = await Api.payments.makeHMOPayment(input.facilityId, input.data)
    if (response && !response.statusCode) {
      const status = response.paymentStatus ? response.paymentStatus.status : EPaymentStatus.DECLINED
      return { status: status === EPaymentStatus.DECLINED ? 'Failed' : 'OK', payment: response, message: [] }
    }
    return { status: 'Failed', message: Array.isArray(response.message) ? response.message : [response.message] }
  }

  @Action
  async getResidentPayment (paymentId: string | number) {
    const response = await Api.payments.getResidentPayment(paymentId)
    this.SET_SELECTED_PAYMENT(response.payment)
    this.SET_SELECTED_PAYMENT_TRANSACTIONS(response.transactions)
  }

  @Action
  resetResidentPayment () {
    this.SET_SELECTED_PAYMENT(null)
    this.SET_SELECTED_PAYMENT_TRANSACTIONS([])
  }

  @Action
  async calculateSchedulePaymentDetails (input: { residentId: number | string, params: ICalculateScheduledPaymentDetailsDTO }) {
    const response = await Api.payments.calculateSchedulePaymentDetails(input.residentId, input.params)
    this.SET_SCHEDULED_PAYMENT_DETAILS(response)
  }

  @Action
  async resetSchedulePaymentDetails () {
    this.SET_SCHEDULED_PAYMENT_DETAILS(null)
  }

  // @Action
  // async saveScheduledPayment (input: { residentId: number | string, data: Partial<IScheduledPayment> }) {
  //   const response = await Api.payments.saveScheduledPayment(input.residentId, input.data)
  //   return response
  // }
  //
  // @Action
  // async fetchScheduledPayment (scheduleId: number | string) {
  //   const response = await Api.payments.getScheduledPayment(scheduleId)
  //   this.SET_SELECTED_SCHEDULED_PAYMENT(response)
  // }

  // @Action
  // resetScheduledPayment () {
  //   this.SET_SELECTED_SCHEDULED_PAYMENT(null)
  // }

  @Action
  async saveAutoPayment (input: { residentId: number | string, data: Partial<IAutoPaySetting> }) {
    return Api.payments.saveAutoPayment(input.residentId, input.data)
  }

  @Action
  async fetchAutoPayment (scheduleId: number | string) {
    const response = await Api.payments.getAutoPayment(scheduleId)
    this.SET_SELECTED_AUTO_PAYMENT(response)
  }

  @Action
  resetAutoPayment () {
    this.SET_SELECTED_AUTO_PAYMENT(null)
  }

  @Action
  async fetchResidentsInCollections () {
    const residents = await Api.resident.getResidentsInCollections()
    this.SET_RESIDENTS_IN_COLLECTIONS(residents)
  }

  @Action
  async fetchCollectionTasksByFilters (filters: ICollectionTasksFilters) {
    const response: ICollectionTasksResult = await Api.collectionFlow.getCollectionTasksByFilters(filters)
    if (response.items) {
      this.SET_COLLECTION_TASKS(response.items)
      this.SET_COLLECTION_TASKS_TOTAL(response.total)
    }
  }

  @Action
  async fetchCollectionTasksWithAgingByFilters (filters: ICollectionTasksFilters) {
    const response = await Api.collectionFlow.getCollectionTasksWithAgingByFilters(filters)
    if (response) {
      this.SET_COLLECTION_WITH_AGING_TASKS(response.items)
      this.SET_COLLECTION_TASKS_WITH_AGING_TOTAL(response.total)
      this.SET_COLLECTION_TASKS_WITH_AGING_TED_DATES(response.transactionEffectiveDates)
    }
  }

  @Action
  setCollectionTaskWithAgingFilters (filters: any) {
   this.SET_COLLECTION_WITH_AGING_FILTERS(filters)
  }

  @Action
  async fetchResidentHistoryByFilters (input: { residentId: number | string, filters: IResidentEventHistoryFiltersDTO }) {
    const { items, total } = await Api.resident.getResidentHistoryByFilters(input.residentId, input.filters)
    if (items) {
      this.SET_COLLECTION_HISTORY(items)
      this.SET_COLLECTION_HISTORY_TOTAL(total)
    }
  }

  @Action
  async scheduleCallWithResident (input: { residentId: number | string, data: Partial<ICollectionTask> }) {
    const response = await Api.collectionFlow.scheduleCallWithResident(input.residentId, input.data)
    if (!(response as any).statusCode) {
      this.UPDATE_RESIDENT_COLLECTION_TASK(response)
      return 'OK'
    }
    return 'Fail'
  }

  @Action
  async takeActionResidentCalled (input: { residentId: number | string, data: ICollectionTaskCalledResidentDTO }) {
    const response = await Api.collectionFlow.takeActionResidentCalled(input.residentId, input.data)
    if (!(response as any).statusCode) {
      this.UPDATE_RESIDENT_COLLECTION_TASK(response)
      return 'OK'
    }
    return 'Fail'
  }

  @Action
  async changeCollectionTaskAssignedUser (input: { collectionTaskId: number | string, assignedUserId: number, assignedUserData: IUser, facilityId: number, residentId: number, currentUserId: number }) {
    const response = await Api.collectionFlow.changeCollectionTaskAssignedUser(input.collectionTaskId, input.assignedUserId, input.facilityId, input.residentId, input.currentUserId)
    if (response === 'OK') {
      this.UPDATE_COLLECTION_TASK_ASSIGNED_USER({ collectionTaskId: parseInt(input.collectionTaskId.toString()), assignedUser: input.assignedUserData })
    }
    return response
  }

  @Action
  async takeActionSendMailToResidentContact (input: { residentId: number | string, data: ICollectionTaskSendMailDTO }) {
    const response = await Api.collectionFlow.takeActionSendMailToResidentContact(input.residentId, input.data)
    if (!(response as any).statusCode) {
      this.UPDATE_RESIDENT_COLLECTION_TASK(response)
      return 'OK'
    }
    return 'Fail'
  }

  @Action
  async changeResidentEscalation (input: { residentId: number | string, data: ICollectionTaskChangeEscalationDTO }) {
    const response = await Api.collectionFlow.changeResidentEscalation(input.residentId, input.data)
    if (!(response as any).statusCode) {
      this.UPDATE_RESIDENT_COLLECTION_TASK(response)
      return 'OK'
    }
    return 'Fail'
  }

  @Action
  async postponeResidentCollection (input: { residentId: number | string, data: ICollectionTaskPostponeDTO }) {
    const response = await Api.collectionFlow.postponeResidentCollection(input.residentId, input.data)
    if (!(response as any).statusCode) {
      this.UPDATE_RESIDENT_COLLECTION_TASK(response)
      return 'OK'
    }
    return 'Fail'
  }

  @Action
  async addResidentHistoryNote (input: { residentId: number | string, data: { user: number, note: string } }) {
    const item = await Api.collectionFlow.addResidentCollectionHistoryNote(input.residentId, input.data)
    if (item.user) {
      this.ADD_ITEM_TO_COLLECTION_HISTORY(item)
      return 'OK'
    }
    return 'Failed'
  }

  @Action
  async addPaymentMethod (input: { residentId: number | string, data: IResidentCreatePaymentMethodDTO }) {
    const response = await Api.resident.createPaymentMethod(input.residentId, input.data)
    if (response.id) {
      this.ADD_PAYMENT_METHOD(response)
      return { status: 'OK', id: response.id }
    }
    const message = (response as any).message
    return { status: 'Failed', message: Array.isArray(message) ? message : [message] }
  }

  @Action
  async getResidentPaymentMethods (residentId: number | string) {
    const response = await Api.resident.getResidentPaymentMethods(residentId)
    if (Array.isArray(response)) {
      this.SET_PAYMENT_METHODS(response)
    }
  }

  @Action
  async makePaymentMethodPrimary (input: { residentId: number | string, paymentMethodId: number }) {
    const response = await Api.resident.makeResidentPaymentMethodPrimary(input.residentId, input.paymentMethodId)
    if (response === 'OK') {
      this.MAKE_PAYMENT_METHOD_PRIMARY(input.paymentMethodId)
    }
  }

  @Action
  async deletePaymentMethod (paymentMethodId: number) {
    const response = await Api.resident.deletePaymentMethod(paymentMethodId)
    if (response === 'OK') {
      this.DELETE_PAYMENT_METHOD(paymentMethodId)
    }
    return response
  }

  @Action
  async fetchResidentCollectionTask (residentId: number | string) {
    const response = await Api.resident.getResidentCollectionTask(residentId)
    this.SET_RESIDENT_COLLECTION_TASK(response)
  }

  // Resident portal actions
  @Action
  async sendInvitationToResidentPortal (data: any) {
    return Api.residentPortal.sendInvitation(data)
  }

  @Action
  async createResidentUserWithInvitation (data: ICreateResidentUserWithTempPasswordDTO) {
    return Api.residentPortal.createResidentUserWithTempPassword(data)
  }

  @Action
  async connectUserToResident (data: any) {
    const response = await Api.residentPortal.connectUserToResident(data)
    if (response.accessToken) {
      Api.setToken(response.accessToken, true)
      return 'OK'
    }
    return response
  }

  @Action
  async fetchUserResidents () {
    const response = await Api.resident.getUserResidents()
    this.UPDATE_RESIDENT_LIST(response)
  }

  @Action
  async fetchChainTypeOfContactListMap () {
    const map = await Api.resident.getChainTypeOfContactListMap()
    this.SET_CHAIN_CONTACT_TYPE_LIST_MAP(map)
  }

  @Action
  async setupFacilityResidentContactSettings (residentContactSettings: ISetupWizardFacilityResidentContactSettings[]) {
    return Api.resident.setupFacilityResidentContactSettings(residentContactSettings)
  }

  @Action
  async editPaymentMethod (input: any) {
    const response = await Api.resident.editPaymentMethod(input.residentId, input.paymentMethodId, input.data)
    if (response.id) {
      this.EDIT_PAYMENT_METHOD(response)
      return { status: 'OK' }
    }
    const message = (response as any).message
    return { status: 'Failed', message: Array.isArray(message) ? message : [message] }
  }

  @Action
  async fetchCollectionTasksCountByFilters (filters: ICollectionTasksFilters) {
    const response: ICollectionTasksResult = await Api.collectionFlow.getCollectionTasksByFilters(filters)
    if (response.items) {
      return response.total
    }
    return 0
  }

  @Action
  async toggleResidentContactEmailForMailing (payload:any) {
    const response = await Api.resident.toggleResidentContactEmailForMailing(payload.residentId, payload.contactId, payload.isEmailUsedForMailing)
    if (response.id) {
      return { status: 'OK' }
    }
    return { status: 'Failed' }
  }

  @Action
  async toggleResidentContactAddressForMailing (payload:any) {
    const response = await Api.resident.toggleResidentContactAddressForMailing(payload.residentId, payload.contactId, payload.isAddressUsedForMailing)
    if (response.id) {
      return { status: 'OK' }
    }
    return { status: 'Failed' }
  }

  @Action
  async updateResidentContactsForMailing (payload: any) {
    const response = await Api.resident.updateResidentContactsForMailing(payload.residentId, payload.payload)
    if (response && response.length) {
      return { status: 'OK' }
    }
    return { status: 'Failed', error: response.message }
  }

  @Action
  async updateCollectionHistory (payload: any) {
    return Api.collectionFlow.updateCollectionHistoryItem(payload.historyId, payload.payload)
  }

  @Action
  async getResidentChainMigrationData (residentId: number) {
    return Api.resident.getResidentChainMigrationData(residentId)
  }

  get getFilters () {
    return this.filters
  }

  get getResidents () {
    return this.residents
  }

  get getTotal () {
    return this.total
  }
}
